/**
 * KeychainExplanationDialog
 *
 * A first-run dialog that explains why macOS will ask for keychain access.
 * Shown before the first keychain-triggering operation (connecting with stored password
 * or MCP server starting with encrypted token).
 *
 * Once dismissed, this dialog won't appear again (flag stored in app settings).
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { KeyRound, Shield, Lock } from 'lucide-react';

interface KeychainExplanationDialogProps {
  open: boolean;
  onContinue: () => void;
}

export function KeychainExplanationDialog({
  open,
  onContinue,
}: KeychainExplanationDialogProps) {
  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" />
            Secure Password Storage
          </DialogTitle>
          <DialogDescription className="text-left">
            Jam SQL Studio uses your Mac's Keychain to securely store database passwords.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium">Why you'll see a prompt</p>
              <p className="text-sm text-muted-foreground">
                macOS will ask for your keychain password the first time the app needs
                to access your saved database credentials.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Lock className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium">Your passwords are encrypted</p>
              <p className="text-sm text-muted-foreground">
                Database passwords are encrypted using Apple's secure keychain
                technology - never stored in plain text.
              </p>
            </div>
          </div>

          <div className="rounded-md bg-muted p-3 text-sm">
            <p className="text-muted-foreground">
              <strong>Tip:</strong> When prompted, select "Always Allow" to avoid
              repeated keychain prompts.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onContinue} className="w-full sm:w-auto">
            Got it, continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
